/* eslint-disable func-names */
import React, { useContext, useEffect, useState } from 'react';
import DrawerDimmingOverlay from 'components/DrawerDimmingOverlay';
import { Context } from 'components/Store';
import {
  SET_REMOVE_PRODUCT_FAILURE,
  SET_TOAST_MESSAGE,
  SET_TOAST_TYPE,
  SET_SHOW_TOAST_REMOVE_PRODUCT
} from 'reducers/reducer';

import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import EmptyTableState from 'components/EmptyTableState';
import NoSearchResultsImage from 'assets/no_search_results_found.svg';
import NoProductsImage from 'assets/no_products_found.svg';
import Toast from 'components/Toast';
import { ERROR_TOAST_TIMEOUT } from 'components/ToastContainer/toastManager';
import Breadcrumb from 'components/App/AppShell/components/Breadcrumb';
import useProducts from 'hooks/useProducts';
import { getServerErrorMessage } from 'helpers/errorHelpers';
import useInventoryProduct from 'screens/ProductList/hooks/useInventoryProduct';
import SortByProductSource from './components/ProductListSourceFilter';
import SortByProductType from './components/ProductListTypeFilter';
import EditProductDrawer from './components/EditProductDrawer';
import { NEW_CUSTOM_PRODUCT_DEFAULTS } from './components/EditProductDrawer/customProductDefaults';
import { PRODUCT_FILTER_OPTIONS } from './helpers/tableConstants';
import ProductListTable, {
  AddProductButton
} from './components/ProductListTable';
import AddProductDrawer from './components/AddProductDrawer';
import SimpleProductSearch from './components/SimpleProductSearch';
import './index.css';
import { ProductListContext } from './context/ProductListProvider';
import {
  buildCustomProductPayload,
  buildMasterlistProductPayload,
  parseFormulation
} from './helpers/formValuesToPayload';

const DRAWER_Z_INDEX = 1000;

// Product rows read the standard unit from the API keys, so an edited product
// has to expose the form value under those keys too.
const toStandardUnitKeys = values => ({
  stdunit: values.standardUnit,
  stdUnit: values.standardUnit
});

// Rows reach the Selected list from three shapes: masterlist search (`id`),
// the inventory list (`productId`) and the custom product form (either). All
// lookups into the list have to agree on one key or they resolve to different
// rows.
const selectedProductKey = product => product?.id ?? product?.productId;

const findSelectedIndex = (selectedProductsList, key) =>
  selectedProductsList.findIndex(pr => selectedProductKey(pr) === key);

const Products = () => {
  const {
    setProductsInUse,
    setIsLoadingProductInUse,
    clearAddProduct,
    tableSearchText,
    setSearchText
  } = useContext(ProductListContext);
  const amplitude = useContext(AmplitudeContext);
  const {
    products,
    reload,
    productsCount,
    isLoading,
    pageNo,
    goToPage,
    sort,
    getAllUseProducts,
    allProducts,
    isLoadingAllProducts,
    filterByNameQuery,
    filterByProductTypes,
    filterBySources,
    sortBy,
    sortDir
  } = useProducts({ sortBy: 'productName', sortDir: 'asc' });

  const {
    addUpdateListPrice,
    addProducts,
    updateCustomProduct
  } = useInventoryProduct();

  const [toastMessage, setToastMessage] = useState('');
  const [toastDetail, setToastDetail] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState(
    /** @type {string | null} */ (null)
  );
  const [productToEdit, setProductToEdit] = useState(null);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [typeFilters, setTypeFilters] = useState([]);
  const [sourceFilters, setSourceFilters] = useState([]);
  const isSearchFilterActive = !!typeFilters.length || !!sourceFilters.length;
  const [showAddProductDrawer, setShowAddProductDrawer] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  const [editProductHeader, setEditProductHeader] = useState('');
  const [editProductSaveText, setEditProductSaveText] = useState('Save');
  const [selectedProductsList, setSelectedProductsList] = useState([]);
  const [hasCreatedCustomProduct, setHasCreatedCustomProduct] = useState(false);
  const [, dispatch] = useContext(Context);

  const refreshProducts = () => {
    reload();
  };

  // The server explains rejections the form cannot predict — an incompatible
  // standard unit, for one — so that reason is surfaced alongside the toast.
  const showSaveFailure = error => {
    setToastType('error');
    setToastMessage('Unable to save changes');
    setToastDetail(getServerErrorMessage(error) ?? null);
    setShowToast(true);
  };

  const handleAddProduct = () => {
    clearAddProduct();
    getAllUseProducts();
    setShowAddProductDrawer(true);
    setIsAddingProducts(true);
  };

  const handleCloseAddProductDrawer = () => {
    clearAddProduct();
    setShowAddProductDrawer(false);
    setIsAddingProducts(false);
    setSelectedProductsList([]);
    // A custom product created during this session is already saved, so
    // cancelling cannot take it back -- the list has to show what now exists.
    if (hasCreatedCustomProduct) {
      setHasCreatedCustomProduct(false);
      refreshProducts();
    }
  };

  const handleTypeFilterChange = selected => {
    filterByProductTypes(selected);
    setTypeFilters(selected);
  };

  const handleSourceFilterChange = selected => {
    filterBySources(selected);
    setSourceFilters(selected);
  };

  const handleOnAddCustomProduct = () => {
    const product = {
      ...NEW_CUSTOM_PRODUCT_DEFAULTS,
      productId: uuidv4(),
      isNew: true,
      custom: true,
      formulation: {}
    };
    setProductToEdit(product);
    setEditProductHeader('Add Custom Product');
    setEditProductSaveText('Create Product');

    setShowEditDrawer(true);
    setShowAddProductDrawer(false);
  };

  const handleOnEdit = product => {
    setEditProductSaveText('Save');
    setProductToEdit(product);
    if (product.custom ?? product.isNew) {
      setEditProductHeader('Edit Custom Product');
    } else if (!showAddProductDrawer && !product.custom) {
      setEditProductHeader('Edit Product');
    } else {
      setEditProductHeader('Add to Product List');
    }
    if (
      isAddingProducts &&
      selectedProductsList.find(prod => prod.id === product.id) &&
      !product.isNew
    ) {
      setEditProductHeader('Edit Product');
    }

    setShowAddProductDrawer(false);
    setShowEditDrawer(true);
  };

  const stageCreatedCustomProduct = async values => {
    // The shared payload builder carries an `id` for the update endpoint; the
    // create contract has no such field and the id here is only a client-side
    // uuid, so it must not travel with the create.
    const createPayload = omit(buildCustomProductPayload(values), 'id');
    const result = await addProducts({ itemsToCreate: [createPayload] });
    const [, productResponse] = result || [];
    const createdProduct = productResponse?.data;

    if (!createdProduct) {
      showSaveFailure(result?.[0]?.error);
      return;
    }

    const persisted = {
      ...values,
      ...createdProduct,
      ...toStandardUnitKeys({ ...values, ...createdProduct }),
      productId: createdProduct.id ?? selectedProductKey(values),
      producttype: createdProduct.type ?? values.type,
      custom: true,
      isNew: false
    };

    setSelectedProductsList([...selectedProductsList, persisted]);
    setHasCreatedCustomProduct(true);
    setSearchText('');
    setShowAddProductDrawer(true);
    setShowEditDrawer(false);
  };

  const handleEditFromAddProduct = async values => {
    // Selecting a masterlist product only stages it -- nothing there reaches
    // the server, so Back leaves the list as it was. A new custom product is
    // different: it is created on this submit, so the user learns about a
    // rejection on the form that produced it.
    const isNewCustomProduct = productToEdit?.isNew === true;
    const editedProductId =
      selectedProductKey(values) ?? selectedProductKey(productToEdit);
    // Resolved once: deriving membership and the replacement slot from separate
    // lookups let them disagree, and splice(-1, …) then overwrote the last row.
    const oldItemIndex = findSelectedIndex(
      selectedProductsList,
      editedProductId
    );
    const isInTheList = oldItemIndex !== -1;

    if (isNewCustomProduct && !isInTheList) {
      await stageCreatedCustomProduct(values);
      return;
    }

    if (!isInTheList) {
      // Select a new product
      setSelectedProductsList([
        ...selectedProductsList,
        values.custom ? { ...values, ...toStandardUnitKeys(values) } : values
      ]);
    } else if (!isNewCustomProduct && !values.custom) {
      // Soft edit selected masterlist product
      const newItem = { ...selectedProductsList[oldItemIndex] };
      newItem.averagePricePerUnit = values.averagePricePerUnit;
      newItem.averagePricePerUnitUnit = values.averagePricePerUnitUnit;
      newItem.packageWeight = values?.packageWeight;
      const newSelectedProducts = [...selectedProductsList];
      newSelectedProducts.splice(oldItemIndex, 1, newItem);
      setSelectedProductsList(newSelectedProducts);
    } else {
      // Soft edit selected custom product
      const newItem = buildCustomProductPayload(values);
      Object.assign(newItem, toStandardUnitKeys(values));
      newItem.isNew = isNewCustomProduct;
      newItem.custom = true;
      newItem.producttype = values.type;
      newItem.productId = editedProductId;
      const newSelectedProducts = [...selectedProductsList];
      newSelectedProducts.splice(oldItemIndex, 1, newItem);
      setSelectedProductsList(newSelectedProducts);
      amplitude.sendEventToAmplitude(
        amplitude.events.epic.ProductList.editCustomProductCart,
        {
          productId: newItem.productId
        }
      );
    }

    setSearchText('');
    setShowAddProductDrawer(true);
    setShowEditDrawer(false);
  };

  const onCloseEditProductDrawer = () => {
    if (isAddingProducts) {
      setShowAddProductDrawer(true);
      setTimeout(() => {
        setShowEditDrawer(false);
      }, 300);
    } else {
      setShowEditDrawer(false);
    }
  };

  const handleEditFromLandingPage = async values => {
    // Hard edit logic
    let failure = null;
    if (values.custom) {
      // Hard add/edit custom product
      const payload = buildCustomProductPayload(values);
      const result = await updateCustomProduct(payload);
      // A rejection can carry no error object, so it needs its own stand-in
      if (!result?.success)
        failure = result?.error ?? new Error('Unable to save changes');
    } else {
      // Hard add/edit masterlist product
      const payload = buildMasterlistProductPayload(values);
      await addUpdateListPrice(payload, undefined, err => {
        failure = err ?? new Error('Unable to save changes');
      });
    }
    // A rejected save leaves the drawer open so the reason can be acted on,
    // and the list is left alone since nothing actually changed.
    if (failure) {
      showSaveFailure(failure);
      return;
    }

    setToastType('success');
    setToastMessage('Changes saved.');
    setToastDetail(null);
    setShowToast(true);
    refreshProducts();

    setShowEditDrawer(false);
    setProductToEdit();
  };

  // The promise has to reach Formik: when onSubmit returns undefined it stops
  // tracking the submission and leaves isSubmitting true, so the Save button
  // spins for good.
  const handleEditProductSubmit = values => {
    if (isAddingProducts) {
      return handleEditFromAddProduct(values);
    }
    return handleEditFromLandingPage(values);
  };

  const handleAddSubmit = async () => {
    if (showAddProductDrawer) {
      const itemsToUpdate = [];

      selectedProductsList
        .filter(x => !x.isNew)
        .forEach(pr => {
          itemsToUpdate.push({
            productId: selectedProductKey(pr),
            averagePricePerUnit: pr.averagePricePerUnit ?? 0,
            averagePricePerUnitUnit: pr.averagePricePerUnitUnit,
            ...(pr?.packageWeight
              ? { packageWeight: Number.parseFloat(pr?.packageWeight) }
              : {})
          });
        });
      const itemsToCreate = selectedProductsList
        .filter(x => x.isNew)
        .map(product => ({
          name: product.name,
          manufacturer: product.manufacturer,
          standardUnit: product.standardUnit,
          type: product.type,
          makeUp: product.makeUp,
          density: product.density,
          stdFactor: product.stdFactor,
          stdPackageUnit: product.stdPackageUnit,
          formulation: parseFormulation(product.formulation),
          averagePricePerUnit: product.averagePricePerUnit,
          averagePricePerUnitUnit: product.averagePricePerUnitUnit,
          organizationId: product.organizationId
        }));
      try {
        await addProducts({ itemsToUpdate, itemsToCreate });
      } catch {
        // Nothing was written, so the drawer keeps the selection for a retry.
        setShowToast(true);
        setToastType('error');
        setToastMessage('Product not added.');
        return;
      }

      // Attribute edits to an already-persisted custom product cannot travel in
      // the add batch, so they follow it. Running them first left a removed
      // product un-hidden when a later call failed, with nothing reporting it.
      const customProductResults = await Promise.all(
        selectedProductsList
          .filter(product => product.custom && !product.isNew)
          .map(product =>
            updateCustomProduct(buildCustomProductPayload(product))
          )
      );
      const failedCustomProduct = customProductResults.find(
        result => !result?.success
      );

      // The products are on the list either way, and holding the selection open
      // would let a retry create a second copy of everything in itemsToCreate.
      setShowAddProductDrawer(false);
      setIsAddingProducts(false);
      clearAddProduct();
      setSelectedProductsList([]);
      refreshProducts();

      if (failedCustomProduct) {
        setToastType('error');
        setToastMessage('Products added, but some details were not saved.');
        setToastDetail(
          getServerErrorMessage(failedCustomProduct.error) ?? null
        );
      } else {
        setToastType('success');
        setToastMessage('Products added to list.');
        setToastDetail(null);
      }
      setShowToast(true);
    }
  };

  useEffect(() => {
    setIsLoadingProductInUse(isLoadingAllProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingAllProducts]);

  useEffect(() => {
    setProductsInUse(allProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProducts]);

  const onCloseHandler = () => {
    dispatch({
      type: SET_TOAST_MESSAGE,
      payload: 'null'
    });
    dispatch({
      type: SET_TOAST_TYPE,
      payload: 'null'
    });
    dispatch({
      type: SET_SHOW_TOAST_REMOVE_PRODUCT,
      payload: false
    });
    dispatch({
      type: SET_REMOVE_PRODUCT_FAILURE,
      payload: false
    });
    setShowToast(false);
  };

  const toastHandler = () => {
    // A failure leaves the user something to read and act on, so it sits as long
    // as the drawer's own error toasts rather than the confirmation default.
    const toastTimeout = toastType === 'error' ? ERROR_TOAST_TIMEOUT : 5000;
    let messageDivText = null;
    if (toastMessage === 'Unable to remove product.') {
      messageDivText =
        'An error prevented your product(s) from being removed from your list. Please try again.';
    } else if (toastMessage === 'Product not added.') {
      messageDivText =
        'An error prevented your product(s) from being added to your list. Please try again.';
    }

    if (messageDivText) {
      return (
        <Toast
          type={toastType}
          isBold
          open={showToast}
          title={toastMessage}
          onClose={onCloseHandler}
          timeout={-1}
        >
          <div>{messageDivText}</div>
          <div className="flex flex-col">
            <Button
              style={{ alignSelf: 'end', marginTop: '8px' }}
              type={ButtonType.primary}
              onClick={() => {
                location.href = 'https://www.syngentadigital.com/us-en';
              }}
            >
              Contact Support
            </Button>
          </div>
        </Toast>
      );
    }

    if (toastDetail) {
      return (
        <Toast
          type={toastType}
          isBold
          open={showToast}
          title={toastMessage}
          onClose={onCloseHandler}
          timeout={toastTimeout}
        >
          <div data-testid="toast-detail">{toastDetail}</div>
        </Toast>
      );
    }

    return (
      <Toast
        type={toastType}
        isBold
        open={showToast}
        title={toastMessage}
        timeout={toastTimeout}
        onClose={onCloseHandler}
      />
    );
  };

  return (
    <div
      className="h-full min-h-0 w-full p-4 flex flex-col"
      data-testid="products"
    >
      <Breadcrumb hideCropSeasonDropdown>
        <Breadcrumb.Item title="Products" value="All Products" isLast />
      </Breadcrumb>
      <div className="flex items-center justify-between mb-4 h-8 shrink-0">
        <div className="flex py-2px h-full">
          <span className="text-xl font-semibold mr-5">Product List</span>
          <div className="mr-4">
            <SimpleProductSearch setSearchText={filterByNameQuery} />
          </div>
          <SortByProductType
            width={246}
            typeFilters={typeFilters}
            handleFilterChange={handleTypeFilterChange}
            typeFilterOptions={PRODUCT_FILTER_OPTIONS}
          />
          <SortByProductSource
            sourceFilters={sourceFilters}
            handleFilterChange={handleSourceFilterChange}
          />
        </div>
        <AddProductButton onClick={handleAddProduct} />
        <AddProductDrawer
          zIndex={DRAWER_Z_INDEX}
          mask={false} // Dimming handled by persistent overlay below
          visible={showAddProductDrawer}
          onClose={handleCloseAddProductDrawer}
          selectedProductsList={selectedProductsList}
          setSelectedProductsList={setSelectedProductsList}
          handleOnEdit={handleOnEdit}
          handleAddProductsSubmit={handleAddSubmit}
          handleOnAddCustomProduct={handleOnAddCustomProduct}
          submitButtonDisabled={!selectedProductsList?.length}
        />
      </div>
      <DrawerDimmingOverlay
        visible={showAddProductDrawer || (showEditDrawer && isAddingProducts)}
        zIndex={DRAWER_Z_INDEX - 2}
        onClick={
          showAddProductDrawer && !showEditDrawer
            ? handleCloseAddProductDrawer
            : undefined
        }
      />
      <EditProductDrawer
        zIndex={DRAWER_Z_INDEX - 1}
        suppressAnimation={isAddingProducts}
        mask={!isAddingProducts} // When adding from drawer, dimming handled by persistent overlay
        visible={showEditDrawer}
        product={productToEdit}
        editProductHeader={editProductHeader}
        saveText={editProductSaveText}
        handleEdit={handleEditProductSubmit}
        onClose={onCloseEditProductDrawer}
      />
      <div className="flex flex-1 min-h-0 min-w-0 justify-center">
        <div
          className={`bg-white min-h-0 min-w-0 flex flex-col justify-center items-center ${
            products.length || isLoading ? 'flex-auto' : 'h-fit'
          } w-full rounded-lg shadow-sm-card overflow-hidden border border-neutral-30`}
        >
          <ProductListTable
            products={products}
            productsCount={productsCount}
            isLoading={isLoading}
            loadingMessage={
              tableSearchText.length ? 'Searching Products...' : null
            }
            pageNo={pageNo}
            setPageNo={goToPage}
            onSort={sort}
            sortBy={sortBy}
            sortDir={sortDir}
            handleOnEdit={handleOnEdit}
            setToastType={setToastType}
            setToastMessage={setToastMessage}
            setShowToast={setShowToast}
            refreshProducts={refreshProducts}
            emptyState={
              isSearchFilterActive || tableSearchText.length ? (
                <EmptyTableState
                  overlayIcon={NoSearchResultsImage}
                  message="We were unable to find any products using the search & filter criteria. Try a different search and/or filter."
                />
              ) : (
                <EmptyTableState
                  overlayIcon={NoProductsImage}
                  title="No Products Found"
                  message="To view your products, either create a production record or
          manually add a product."
                  button={<AddProductButton onClick={handleAddProduct} />}
                />
              )
            }
          />

          {toastHandler()}
        </div>
      </div>
    </div>
  );
};
export default Products;
